// Library page for personalizing the Poppy host voice

import React, { useCallback, useMemo, useState } from 'react';
import SEO from '../../../seo/SEO';
import Poppy from '../../../components/avatars/Poppy';
import { useAuth, useSystemWindowSize, useUserSave } from '../../../hooks';
import {
  useLibraryDialog,
  useLibraryPoppyDialLayout,
  useLibraryPoppyDialOptions,
  useLibraryPoppyVoicePreview,
} from '../hooks';
import { analyticsService } from '../../../services/analyticsService';
import { describeArc, polarToCartesian } from '../../../utilities/geometry';
import {
  findEnabledAccents,
  findEnabledHostStyles,
  findHostStyleConfig,
} from '@airhead-games/shared/utilities';
import type {
  HostStyleConfig,
  UserAuthFields,
  UserPreferences,
} from '@airhead-games/shared/types';
import './PoppyPage.css';

type SegmentColorStyle = React.CSSProperties & {
  '--segment-color': string;
};

const getSegmentColorStyle = (color: string): SegmentColorStyle => ({
  '--segment-color': color,
});

const GUEST_HOST_PREFERENCES: UserPreferences = {
  language: 'en',
  accent: 'en-US',
  hostStyle: {
    personality: 'comedian',
    voiceActor: 'Kore',
    gender: 'female',
  },
  contentStyle: 'standard',
};

const toHostPreferencesState = (
  user?: UserAuthFields | null
): UserPreferences => {
  return {
    ...(user?.userPreferences ?? GUEST_HOST_PREFERENCES),
    hostStyle: {
      ...(user?.userPreferences.hostStyle ?? GUEST_HOST_PREFERENCES.hostStyle),
      personality: (user?.userPreferences.hostStyle.personality ??
        GUEST_HOST_PREFERENCES.hostStyle
          .personality) as HostStyleConfig['personality'],
    },
  };
};

const LibraryPoppyPage: React.FC = () => {
  const { user } = useAuth();
  const { openDialog } = useLibraryDialog();
  const [userPreferences, setUserPreferences] = useState<UserPreferences>(() =>
    toHostPreferencesState(user)
  );
  const { markDirty } = useUserSave({
    value: userPreferences,
    setValue: setUserPreferences,
    toUpdate: (value) => ({
      userPreferences: value,
    }),
    fromUser: toHostPreferencesState,
    errorMessage: 'Unable to save your host preferences.',
  });
  const accents = useMemo(
    () => findEnabledAccents(userPreferences.language),
    [userPreferences.language]
  );
  const hostStyles = useMemo(() => findEnabledHostStyles(), []);

  const personalityOptionsRaw = useMemo(() => {
    const seen = new Map<string, string>();
    hostStyles.forEach((style) => {
      if (!seen.has(style.personality)) {
        seen.set(style.personality, style.label);
      }
    });
    return Array.from(seen.entries()).map(([id, label]) => ({
      id,
      label,
    }));
  }, [hostStyles]);

  const accentOptionsRaw = useMemo(
    () =>
      accents.map((accent) => ({
        id: accent.value,
        label: accent.label,
      })),
    [accents]
  );

  const genderOptionsRaw = useMemo(
    () => [
      { id: 'male', label: 'Male' },
      { id: 'female', label: 'Female' },
    ],
    []
  );

  const [hoveredRing, setHoveredRing] = useState<Record<string, string | null>>(
    {
      personality: null,
      accent: null,
      gender: null,
    }
  );

  const clearAllHover = useCallback(() => {
    setHoveredRing({
      personality: null,
      accent: null,
      gender: null,
    });
  }, []);

  const handlePointerEnter =
    (ringId: string, optionId: string) =>
    (event: React.PointerEvent<SVGPathElement>) => {
      if (event.pointerType !== 'mouse') return;
      setHoveredRing((prev) => ({ ...prev, [ringId]: optionId }));
    };

  const handlePointerLeave =
    (ringId: string) => (event: React.PointerEvent<SVGPathElement>) => {
      if (event.pointerType !== 'mouse') return;
      setHoveredRing((prev) => ({ ...prev, [ringId]: null }));
    };

  const handlePointerDown =
    () => (event: React.PointerEvent<SVGPathElement>) => {
      if (event.pointerType === 'mouse') return;
      clearAllHover();
    };

  const { width: windowWidth } = useSystemWindowSize();

  const { personalityOptions, accentOptions, genderOptions } =
    useLibraryPoppyDialOptions({
      personalityOptionsRaw,
      accentOptionsRaw,
      genderOptionsRaw,
    });

  const handlePersonalitySelect = (personality: string) => {
    if (!user) {
      openDialog('login');
      return;
    }

    const style = findHostStyleConfig(
      personality as HostStyleConfig['personality'],
      userPreferences.hostStyle.gender
    );
    if (!style) return;
    analyticsService.trackPreferenceChanged({
      preference_type: 'host_personality',
      preference_value: personality,
      link_location: 'library_poppy',
    });
    setUserPreferences((prev) => ({
      ...prev,
      hostStyle: {
        ...prev.hostStyle,
        personality: style.personality,
        voiceActor: style.voiceActor,
      },
    }));
    markDirty();
  };

  const handleGenderSelect = (gender: string) => {
    if (!user) {
      openDialog('login');
      return;
    }

    const style = findHostStyleConfig(
      userPreferences.hostStyle.personality as HostStyleConfig['personality'],
      gender as HostStyleConfig['gender']
    );
    if (!style) return;
    analyticsService.trackPreferenceChanged({
      preference_type: 'host_gender',
      preference_value: gender,
      link_location: 'library_poppy',
    });
    setUserPreferences((prev) => ({
      ...prev,
      hostStyle: {
        ...prev.hostStyle,
        gender: style.gender,
        voiceActor: style.voiceActor,
      },
    }));
    markDirty();
  };

  const handleAccentSelect = (accent: string) => {
    if (!user) {
      openDialog('login');
      return;
    }

    analyticsService.trackPreferenceChanged({
      preference_type: 'accent',
      preference_value: accent,
      link_location: 'library_poppy',
    });
    setUserPreferences((prev) => ({
      ...prev,
      accent: accent as UserPreferences['accent'],
    }));
    markDirty();
  };

  const { libraryPoppyPlaybackState } = useLibraryPoppyVoicePreview({
    user,
    userPreferences,
    onRequireLogin: () => openDialog('login'),
  });

  const { dialSize, poppySize, center, rings } = useLibraryPoppyDialLayout({
    windowWidth,
    userPreferences,
    hoveredRing,
    personalityOptions,
    accentOptions,
    genderOptions,
    onPersonalitySelect: handlePersonalitySelect,
    onAccentSelect: handleAccentSelect,
    onGenderSelect: handleGenderSelect,
  });
  return (
    <>
      <SEO />
      <section className="host-customization-dial-layered flex min-h-full flex-col bg-muted/30 text-foreground">
        <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col items-center justify-center gap-5 px-4 py-6 sm:px-6 sm:py-8 lg:gap-6 lg:px-8">
          <header className="max-w-2xl text-center">
            <h1 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
              Personalize Poppy
            </h1>
            <p className="mt-3 text-sm leading-6 text-muted-foreground sm:text-base">
              Customize your host of Airhead Games
            </p>
          </header>

          <div className="dial-layered-shell">
            <div className="dial-layered-poppy">
              <Poppy
                size={poppySize}
                disableMenu
                enableGlow
                playbackState={libraryPoppyPlaybackState}
              />
            </div>
            <svg
              className="dial-layered-svg"
              viewBox={`0 0 ${dialSize} ${dialSize}`}
              role="img"
              aria-label="Layered host customization dial"
            >
              {rings.map((ring) => {
                const sweep = 360 / Math.max(ring.options.length, 1);
                const selectedIndex = Math.max(
                  ring.options.findIndex(
                    (option) => option.id === ring.selectedId
                  ),
                  0
                );
                const hoveredIndex = ring.options.findIndex(
                  (option) => option.id === ring.hoveredId
                );
                const highlightIndex = hoveredIndex >= 0 ? hoveredIndex : -1;
                const baseAngle = 0;
                const offsetAngle = -selectedIndex * sweep;
                const angleForIndex = (index: number) =>
                  baseAngle + offsetAngle + index * sweep;

                return (
                  <g key={ring.id}>
                    <circle
                      className="dial-layered-base"
                      cx={center}
                      cy={center}
                      r={ring.radius}
                      strokeWidth={ring.thickness}
                    />

                    <path
                      className="dial-layered-highlight"
                      style={getSegmentColorStyle(
                        ring.options[selectedIndex]?.color ?? '#ffffff'
                      )}
                      d={describeArc(
                        center,
                        center,
                        ring.radius,
                        angleForIndex(selectedIndex) - sweep / 2,
                        angleForIndex(selectedIndex) + sweep / 2
                      )}
                      strokeWidth={ring.thickness}
                    />

                    {highlightIndex >= 0 &&
                      highlightIndex !== selectedIndex && (
                        <path
                          className="dial-layered-highlight is-hover"
                          style={getSegmentColorStyle(
                            ring.options[highlightIndex]?.color ?? '#ffffff'
                          )}
                          d={describeArc(
                            center,
                            center,
                            ring.radius,
                            angleForIndex(highlightIndex) - sweep / 2,
                            angleForIndex(highlightIndex) + sweep / 2
                          )}
                          strokeWidth={ring.thickness}
                        />
                      )}

                    {ring.options.map((option, index) => {
                      const midAngle = angleForIndex(index);
                      const startAngle = midAngle - sweep / 2;
                      const endAngle = midAngle + sweep / 2;
                      const labelPadding = sweep * 0.12;
                      const labelStartAngle = startAngle + labelPadding;
                      const labelEndAngle = endAngle - labelPadding;
                      const labelPathId = `dial-label-${ring.id}-${option.id}`;
                      const labelRadius = ring.radius;
                      const lineStart = polarToCartesian(
                        center,
                        center,
                        ring.radius - ring.thickness / 2,
                        startAngle
                      );
                      const lineEnd = polarToCartesian(
                        center,
                        center,
                        ring.radius + ring.thickness / 2,
                        startAngle
                      );

                      return (
                        <g key={option.id}>
                          <line
                            className="dial-layered-divider"
                            x1={lineStart.x}
                            y1={lineStart.y}
                            x2={lineEnd.x}
                            y2={lineEnd.y}
                          />
                          <path
                            className="dial-layered-hit"
                            d={describeArc(
                              center,
                              center,
                              ring.radius,
                              startAngle,
                              endAngle
                            )}
                            strokeWidth={ring.thickness}
                            onPointerEnter={handlePointerEnter(
                              ring.id,
                              option.id
                            )}
                            onPointerLeave={handlePointerLeave(ring.id)}
                            onPointerDown={handlePointerDown()}
                            onClick={() => ring.onSelect(option.id)}
                          />
                          <path
                            id={labelPathId}
                            className="dial-layered-label-path"
                            d={describeArc(
                              center,
                              center,
                              labelRadius,
                              labelStartAngle,
                              labelEndAngle
                            )}
                          />
                          <text
                            className="dial-layered-label"
                            textAnchor="middle"
                          >
                            <textPath
                              href={`#${labelPathId}`}
                              startOffset="50%"
                              dominantBaseline="middle"
                            >
                              {option.label}
                            </textPath>
                          </text>
                        </g>
                      );
                    })}

                    <circle
                      className="dial-layered-edge"
                      cx={center}
                      cy={center}
                      r={ring.radius + ring.thickness / 2}
                    />
                    <circle
                      className="dial-layered-edge"
                      cx={center}
                      cy={center}
                      r={ring.radius - ring.thickness / 2}
                    />
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </section>
    </>
  );
};

export default LibraryPoppyPage;
